/**
 * The Trendy tab — KANON `Detal klienta v3`, variant a. One date range over three sections.
 *
 * ── One range, and it is free rather than a set of presets ────────────────────────────────────
 * The owner's decision, carried over from mobile and restated in the canon's own caption: "to
 * wolny zakres, nie zestaw presetów — trener porównuje dwa dowolne tygodnie". So the control is two
 * date fields, and every read on the tab takes the same `from`/`to` — including the attendance
 * fold, which is new in v3 and which the range bar's own sentence promises to govern.
 *
 * ── FREKWENCJA is drawn here now, and it costs no request ─────────────────────────────────────
 * v2's round refused this card because the tab did not read `/consistency` and that round was not
 * allowed to add a call. v3 draws it and the owner's instruction is to take what v3 draws, so it is
 * here — and the read is the same `queryOptions` the shell already pays for, so at the default
 * thirty-day range the card is answered out of cache and the tab makes no extra request at all. A
 * range the trainer moves is a different key and a different read, which is the honest cost of the
 * control governing this card.
 *
 * ── The three charts share one readout ────────────────────────────────────────────────────────
 * Owner, 31 August 2026: the question this tab exists to answer is "attendance dropped — did sleep
 * drop the same week?". Hovering any of tonnage, sleep or mood moves the crosshair on all three, to
 * the same instant, and `view.xDomain` is handed to each so one date lands on one pixel column. The
 * mechanism is `shared/ui/chart/readoutSync`, which deliberately keeps the pointer's position out
 * of React state so a pointer move re-renders two charts and not the tab.
 *
 * ── The silhouette is the MOBILE app's figure, and is exempt from pixel fidelity ──────────────
 * The canon draws a blocky body of its own out of rounded rectangles; the owner's instruction is
 * that the anatomical one ships — "ma odpowiadać, nie być identyczne" — and, on 12 August 2026,
 * that it is the FIGURE THE PHONE DRAWS.
 */

import { RadioGroup } from '@base-ui/react/radio-group';
import { Radio } from '@base-ui/react/radio';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'motion/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useWellbeingLevels, wellbeingBanding } from '~/entities/client';
import { screenMotion } from '~/shared/design-system/motion';
import { type SkeletonGate, useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { BodySilhouette } from '~/shared/ui/bodyModel/BodySilhouette';
import { EmptyState } from '~/shared/ui/EmptyState';
import {
  CalendarMarkIcon,
  VolumeMarkIcon,
} from '~/shared/ui/EmptyStateIcons';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import { ChartCardShell } from '~/shared/ui/chart/ChartCardShell';
import { ReadoutChart, type ChartBanding } from '~/shared/ui/chart/ReadoutChart';
import { ReadoutRing } from '~/shared/ui/chart/ReadoutRing';
import { ReadoutSyncProvider } from '~/shared/ui/chart/readoutSync';

import {
  clientConsistencyQueryOptions,
  clientMuscleVolumeQueryOptions,
  clientProfileQueryOptions,
  clientTrendsQueryOptions,
} from '../api/clientDetailQueries';
import {
  toAttendanceDays,
  toClientGender,
  toMuscleGroups,
  toTrendsSource,
} from '../api/clientDetailSource';
import { buildActivityRings } from '../model/activityRings';
import { buildAttendance, type AttendanceDayCell } from '../model/attendance';
import {
  buildMuscleVolume,
  silhouetteGender,
  splitZoneLegend,
  type SilhouetteRegion,
  type VolumeZone,
} from '../model/muscleVolume';
import { buildTrends, type TrendScale, type TrendSeries } from '../model/trends';
import { decideTrendsRange, defaultTrendsRange, type TrendsRange } from '../model/trendsRange';
import { DateRangeBar } from './DateRangeBar';
import { usePublishTabMeta } from './detailTabMeta';
import { SectionHead } from './SectionHead';
import eyebrows from './cardEyebrow.module.css';
import styles from './DetailTab.module.css';

/**
 * The two rings' geometry and their ink.
 *
 * One size for both, because they are two instances of one block on two sections: 74px across with
 * a 10px band. Numbers rather than tokens because `RingChart` computes its geometry in JavaScript —
 * see `shared/ui/chart/ReadoutRing`. Nothing reserves this ring's box in a stylesheet, so there is
 * no token for it either: `--sz-att-ring` existed, was read by nothing, and went on 2 September 2026.
 *
 * The inks are by MEANING, from `vendor.css`: attendance is a completion, so it wears `--good`;
 * volume has no hue of its own and takes neutral ink. The accent is in neither — ember marks a
 * thing to act on, and a summary figure is not one.
 */
const RING = { diameter: 74, thickness: 10, durationMs: 950 } as const;

/**
 * One class per day state. Three drawings, never three hues of one — see the CSS.
 *
 * The WORDS are `web:trainingWeek.state.*`, which `shared/ui/TrainingWeekStrip` reads too: the
 * roster's week, the summary column's week and this grid all name a day with the same noun.
 */
const DAY_CLASS: Readonly<Record<AttendanceDayCell['state'], string>> = {
  done: 'attCellDone',
  missed: 'attCellMissed',
  free: 'attCellFree',
};

interface TrendsTabProps {
  readonly clientId: number;
  readonly reducedMotion: boolean;
}

/**
 * The four zone colours, as token references — the canon's legend, and what the two SILHOUETTES are
 * painted from.
 *
 * The bars and the legend swatches do not read this. They wear `.{zone}Fill` in the stylesheet,
 * because a class is what a `<span>` wants; the figure needs a value, because an SVG shape takes its
 * `fill` as an attribute. So the two lists must name the same four tokens, and they are the one
 * place on this tab where a colour is written twice. Change one and change the other.
 *
 * `high` is the ACCENT, not the danger red: the canon's own `ZC` table maps "za dużo" to ember. It
 * is the one place on this tab the accent is spent, and it marks a real state.
 *
 * `none` is in the table rather than special-cased: an unstimulated muscle is painted the neutral
 * body colour, which is what "no stimulus" looks like, and having it here means the figure has one
 * code path instead of a rule about which zones are drawable.
 */
const ZONE_FILL: Record<VolumeZone, string> = {
  none: 'var(--bg-sunken)',
  low: 'var(--warn)',
  opt: 'var(--good)',
  high: 'var(--accent)',
};

const ZONES = ['none', 'low', 'opt', 'high'] as const;

/**
 * The regions, as the colour-per-muscle map the figure takes.
 *
 * `buildMuscleVolume` emits at most one region per muscle — two entries naming one shape would be
 * two answers about the same muscle, and the last one written would win silently.
 */
function fillsFor(regions: readonly SilhouetteRegion[]): Record<string, string> {
  return Object.fromEntries(regions.map((region) => [region.muscle, ZONE_FILL[region.zone]]));
}

/**
 * The range control, and the sections it gates.
 *
 * ── A refused range fetches nothing, and that is structural rather than a flag ─────────────────
 * The content below is a child that OWNS every query, so a range the model refuses simply does not
 * mount it — the same construction the screen uses for its tabs. What would be wrong here is
 * fetching anyway and hiding the result: the numbers a two-day window returns are internally
 * consistent, so a chart drawn from them looks exactly like a correct one.
 */
export function TrendsTab({ clientId, reducedMotion }: TrendsTabProps) {
  const { t, i18n } = useTranslation();
  const [range, setRange] = useState(() => defaultTrendsRange(new Date()));

  // Both bounds and the refusal are one decision, in `model/`, because the reason a two-day window
  // is refused is arithmetic the BACKEND does — not a preference this component holds.
  const bounds = decideTrendsRange(range, { t, today: new Date() });

  const dayMonth = new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'long' });
  usePublishTabMeta(
    t('web:clientDetail.tabMeta.range', {
      from: dayMonth.format(new Date(`${range.from}T00:00:00`)),
      to: dayMonth.format(new Date(`${range.to}T00:00:00`)),
    }),
  );

  return (
    <div className={styles.tab}>
      <DateRangeBar
        range={range}
        bounds={bounds}
        onChange={setRange}
        note={t('web:clientDetail.trends.rangeNote', { count: bounds.spanDays })}
      />

      {bounds.valid ? (
        <TrendsContent
          clientId={clientId}
          range={range}
          spanDays={bounds.spanDays}
          reducedMotion={reducedMotion}
        />
      ) : null}
    </div>
  );
}

interface TrendsContentProps {
  readonly clientId: number;
  /** Already validated by `decideTrendsRange`. This component is not mounted for anything else. */
  readonly range: TrendsRange;
  readonly spanDays: number;
  readonly reducedMotion: boolean;
}

function TrendsContent({ clientId, range, spanDays, reducedMotion }: TrendsContentProps) {
  const { t, i18n } = useTranslation();
  /**
   * The two wellbeing cards choose their scale INDEPENDENTLY, and that is deliberate rather than a
   * missed simplification. They are two different questions — "is this client sleeping enough" is
   * usually a weekly question and "did that one brutal session leave them flat" is a per-session
   * one — and a trainer comparing a weekly sleep average against every energy reading is making a
   * comparison the range bar already allows. One shared switch would be a rule about how the two
   * must be read together, which nobody asked for.
   *
   * It lives HERE rather than inside the card because the projection is the model's: `buildTrends`
   * decides what "the previous one" means at each scale, and a card that toggled its own view
   * would have to be handed both projections and pick — which is the decision moving into the
   * component the long way round.
   */
  const [sleepScale, setSleepScale] = useState<TrendScale>('weekly');
  const [moodScale, setMoodScale] = useState<TrendScale>('weekly');
  const trends = useQuery(clientTrendsQueryOptions(clientId, range));
  const volume = useQuery(clientMuscleVolumeQueryOptions(clientId, range));
  const consistency = useQuery(clientConsistencyQueryOptions(clientId, range));
  // ONE GATE PER READ, and that is the 21 September 2026 correction.
  //
  // Frekwencja and objętość used to share a gate over the union of their two reads while each card
  // went on branching on its own `isPending`. That worked only because the branch was the read's:
  // the moment the branch became the gate's, a union would have held one card's bars alive until
  // the OTHER card's endpoint answered — frekwencja waiting on `/muscle-volume`, which is the
  // partial-answer shape inverted. What the union bought was a shared 150ms grace window, and that
  // survives the split for free: both reads open in the same render, so both graces coincide. What
  // each card gains is its own minimum life, measured from its own bars.
  const attendanceGate = useSkeletonGate(consistency.isPending);
  const volumeGate = useSkeletonGate(volume.isPending);
  // The three charts come off a third read, and two of them are banded by the trainer's own levels
  // for "low" (attention-queue R25) — opened in this same render, and waited for with it, so a line
  // is never drawn under the default level and repainted under the trainer's.
  const wellbeing = useWellbeingLevels();
  const levels = wellbeing.levels;
  const chartsGate = useSkeletonGate(trends.isPending || wellbeing.pending);
  // One field of one row: which body to draw. A profile read still in flight, or one that failed,
  // leaves the figure at the default rather than withholding it — the zones are the information
  // here, and a client whose sex nobody recorded is drawn as the default on the phone too.
  const profile = useQuery(clientProfileQueryOptions(clientId));

  const view = buildTrends(trends.isSuccess ? toTrendsSource(trends.data) : null, {
    locale: i18n.language,
    t,
    sleepScale,
    moodScale,
  });
  const muscles = buildMuscleVolume(volume.isSuccess ? toMuscleGroups(volume.data) : null, { t });
  const gender = silhouetteGender(profile.isSuccess ? toClientGender(profile.data) : null);
  const attendance = consistency.isSuccess
    ? buildAttendance(toAttendanceDays(consistency.data) ?? [], spanDays)
    : null;
  const rings = buildActivityRings({
    attendance: attendance?.window ?? null,
    volume: volume.isSuccess ? muscles : null,
  });
  const ringOfAttendance = rings.attendance;
  // `UTC`, because a `YYYY-MM-DD` is a calendar date and parsing it IS the UTC-midnight parse. A
  // browser-local reading would print the day before for every trainer west of Greenwich.
  const dayDate = new Intl.DateTimeFormat(i18n.language, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

  return (
    <>
      <SectionHead
        title={t('web:clientDetail.attendance.eyebrow')}
        aside={t('web:clientDetail.attendance.sectionCaption', { days: spanDays })}
        reducedMotion={reducedMotion}
      />

      {/* The reserve is on the three states that draw a sentence, not on the card itself, so a
        * loaded card is still sized by its own ring and grid. Without it this card stood 48px tall
        * while `/consistency` was open and 147 after it answered, carrying the two chart cards
        * under it 96px down in the frame it landed. */}
      <section
        className={`${styles.card} ${attendanceGate.waiting || ringOfAttendance === null ? styles.reserveAttendance : ''}`}
        aria-busy={attendanceGate.waiting}
      >
        {/* The GATE, not `attendance === null`: the projection is null while the read is open AND
          * when it succeeds with nothing, and a data test cannot separate the two. It read as
          * "loading" for the second case. The wait is the gate's so the ring does not arrive at
          * full opacity 300ms before the bars it replaces are retired.
          *
          * The WAIT is tested first and the failure sits inside the not-waiting arm: the failure
          * is this read's own answer, so it replaces the bars on the hand-over class like the ring
          * does, and `aria-busy` above never reads true over a settled „nie udało się". */}
        {attendanceGate.waiting ? (
          <>
            <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
            {attendanceGate.skeleton ? (
              <span className={styles.skelRingRow}>
                <i className={`${String(skel.bar)} ${String(skel.disc)} ${String(styles.skelRing)}`} />
                <span className={styles.skelRingText}>
                  <i className={`${String(skel.bar)} ${String(styles.skelLead)}`} />
                  <i className={`${String(skel.bar)} ${String(styles.skelLine)}`} />
                </span>
              </span>
            ) : null}
          </>
        ) : consistency.isError ? (
          <p className={`${String(styles.notice)} ${attendanceGate.reveal}`}>
            {t('web:clientDetail.attendance.unavailable')}
          </p>
        ) : attendance === null || ringOfAttendance === null ? (
          // Not a ring at zero. Nothing in this window asked anything of the client, so there is no
          // fraction to draw — see `model/activityRings` — and a grid of thirty rest days beside
          // the absence would be a picture of the same nothing. `attendance === null` reaches here
          // only for a read that SUCCEEDED and produced nothing, the two branches above having
          // taken the failure and the wait.
          <EmptyState
            className={attendanceGate.reveal}
            mark={<CalendarMarkIcon />}
            tone="neutral"
            title={t('web:clientDetail.attendance.empty')}
          />
        ) : (
          <div className={`${String(styles.attRow)} ${attendanceGate.reveal}`}>
            {/* One ring, its percentage in the middle, the three counters beside it and the
              * denominator in words underneath. Owner, 1 September 2026, of the concentric pair
              * this replaces: "ja nie wiem co ona przedstawia aktualnie zupełnie." Two arcs on one
              * centre with their words in a side legend never said which arc was which, and the
              * percentage was on neither. The volume ring moved to the section it is about. */}
            <section className={styles.ringBlock}>
                <em className={styles.ringEyebrow}>
                  {t('web:clientDetail.attendance.rings.attendanceName')}
                </em>
                <div className={styles.ringRow}>
                  <div className={styles.ringColumn}>
                    <ReadoutRing
                      percent={ringOfAttendance.pct}
                      diameter={RING.diameter}
                      thickness={RING.thickness}
                      durationMs={RING.durationMs}
                      scale="wide"
                      colour="var(--chart-series-goal)"
                      figure={t('web:clientDetail.attendance.rings.pct', {
                        pct: ringOfAttendance.pct,
                      })}
                      label={t('web:clientDetail.attendance.rings.attendance', {
                        value: ringOfAttendance.value,
                        total: ringOfAttendance.total,
                        pct: ringOfAttendance.pct,
                      })}
                      reducedMotion={reducedMotion}
                    />
                    {/* The denominator, in words, under the arc. A percentage on its own is a
                      * figure with nothing behind it — 69% of sixteen days and 69% of three are
                      * not the same reading. */}
                    <p className={styles.ringDenominator}>
                      {t('web:clientDetail.attendance.rings.ofDays', {
                        value: ringOfAttendance.value,
                        count: ringOfAttendance.total,
                      })}
                    </p>
                  </div>

                  <dl className={styles.counters}>
                    {(
                      [
                        ['done', ringOfAttendance.value],
                        ['missed', ringOfAttendance.total - ringOfAttendance.value],
                        ['planned', ringOfAttendance.total],
                      ] as const
                    ).map(([key, count]) => (
                      <div className={styles.counter} key={key}>
                        <dt className={styles.counterName}>
                          {t(`web:clientDetail.attendance.counters.${key}`)}
                        </dt>
                        <dd className={styles.counterValue}>{count}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
            </section>

            <div className={styles.attGrid}>
              {/* One square per CALENDAR DAY, and the copy says so. `/consistency` emits one verdict
                * per day, so "sesja" was never the honest word for a square — a client who trained
                * twice on a Tuesday still contributes one. Owner, 1 September 2026. */}
              <ul
                className={styles.attCells}
                aria-label={t('web:clientDetail.attendance.grid.label', {
                  days: spanDays,
                  done: attendance.window.done,
                  missed: attendance.window.total - attendance.window.done,
                  free: attendance.days.length - attendance.window.total,
                })}
              >
                {attendance.days.map((day, index) => (
                  <motion.li
                    key={day.date}
                    className={`${String(styles.attCell)} ${String(styles[DAY_CLASS[day.state]])}`}
                    title={t('web:trainingWeek.cell', {
                      date: dayDate.format(new Date(`${day.date}T00:00:00Z`)),
                      state: t(`web:trainingWeek.state.${day.state}`),
                    })}
                    {...screenMotion('cell', { reducedMotion, index })}
                  >
                    {/* The date under every seventh square. A finger can be put on a day without
                      * counting squares, and thirty labels would be as unreadable as none. */}
                    {day.anchor ? (
                      <em className={styles.attCellDate} aria-hidden="true">
                        {dayDate.format(new Date(`${day.date}T00:00:00Z`))}
                      </em>
                    ) : null}
                  </motion.li>
                ))}
              </ul>

              {/* Four states carried by four DRAWINGS and named in words — the roster preview's own
                * vocabulary and its own tokens, so the two places in this app that draw a training
                * day draw it the same way. Three of the four appear here: the window ends today, so
                * no day in it is still ahead of the client, and `/consistency` has no word for one
                * in any case. */}
              <ul className={styles.attLegend} aria-label={t('web:trainingWeek.legendLabel')}>
                {(['done', 'missed', 'free'] as const).map((state) => (
                  <li className={styles.attLegendItem} key={state}>
                    <span
                      className={`${String(styles.attCell)} ${String(styles[DAY_CLASS[state]])} ${String(styles.attSwatch)}`}
                      aria-hidden="true"
                    />
                    {t(`web:trainingWeek.state.${state}`)}
                  </li>
                ))}
                <li className={styles.attLegendNote}>
                  {t('web:clientDetail.attendance.grid.caption')}
                </li>
              </ul>
            </div>
          </div>
        )}
      </section>

      {/* One provider around the three charts that share a time axis — and only those three. The
        * measurement chart on Pomiary and the lift charts on Rekordy are different spans about
        * different things, and a shared crosshair between them would claim a correspondence that
        * does not exist.
        *
        * `view.xDomain` is handed to all three so the same date lands on the same pixel in each,
        * which is what makes a crosshair synchronised BY INSTANT look synchronised. Without it the
        * three axes fit three different spans and the marks stand at three different places while
        * all three panels correctly read the same day. */}
      <ReadoutSyncProvider>
        <div className={styles.chartGrid}>
          <ChartCard
            id="tonnage"
            eyebrow={t('web:clientDetail.trends.tonnageEyebrow')}
            caption={t('web:clientDetail.trends.tonnageCaption')}
            series={view.tonnage}
            colour="var(--chart-series-neutral)"
            unit={t('clients.detail.progress.tonnage.unit')}
            // Tonnage has a real zero and the mark's length IS the figure, so the axis starts there.
            zeroBaseline
            xDomain={view.xDomain}
            failed={trends.isError}
            gate={chartsGate}
            reducedMotion={reducedMotion}
          />
          <ChartCard
            id="sleep"
            eyebrow={t('web:clientDetail.trends.sleepEyebrow')}
            caption={t(
              sleepScale === 'weekly'
                ? 'web:clientDetail.trends.sleepCaption'
                : 'web:clientDetail.trends.sleepCaptionReadings',
            )}
            series={view.sleep}
            colour="var(--chart-series-sleep)"
            banding={wellbeingBanding('var(--chart-series-sleep)', 'sleep', levels)}
            unit={t('clients.detail.progress.sleep.hour')}
            // Nobody sleeps zero hours. An axis from zero puts every week this client has ever had
            // in the top fifth of the box and says only "the number is around seven".
            zeroBaseline={false}
            xDomain={view.xDomain}
            scale={sleepScale}
            onScaleChange={setSleepScale}
            failed={trends.isError}
            gate={chartsGate}
            reducedMotion={reducedMotion}
          />
          <ChartCard
            id="mood"
            eyebrow={t('web:clientDetail.trends.energyEyebrow')}
            caption={t(
              moodScale === 'weekly'
                ? 'web:clientDetail.trends.energyCaption'
                : 'web:clientDetail.trends.energyCaptionReadings',
            )}
            series={view.mood}
            // Ember, on the owner's 02.09 instruction — it replaced the violet the 31.08 palette
            // gave this series. See the note beside `--chart-series-mood` in `shared/vendor/vendor.css`.
            colour="var(--chart-series-mood)"
            banding={wellbeingBanding('var(--chart-series-mood)', 'mood', levels)}
            // No unit: a 1-10 rating has no dimension, and the caption names the scale.
            zeroBaseline={false}
            xDomain={view.xDomain}
            scale={moodScale}
            onScaleChange={setMoodScale}
            failed={trends.isError}
            gate={chartsGate}
            reducedMotion={reducedMotion}
          />
        </div>
      </ReadoutSyncProvider>

      <SectionHead
        title={t('web:clientDetail.volume.eyebrow')}
        aside={t('web:clientDetail.volume.caption')}
        reducedMotion={reducedMotion}
      />

      {/* The biggest single movement on the screen before this: 486px, in one frame, half a second
        * after the tab opened (measured 1 September 2026). `clusters.length === 0` is true both
        * while `/muscle-volume` is open and when it answers that nothing was lifted, so the card
        * asserted "brak zarejestrowanej objętości" about a client whose request had not come back
        * — and stood one line tall where the silhouette pair was about to be.
        *
        * ── The reserve is on PENDING alone, and that is the exception to the rule above ────────
        * Every other reserve on this screen covers the empty answer too, because a card that
        * shrinks when its answer arrives drags everything under it upward. This card is the LAST
        * thing on the tab, so nothing is under it to drag — and a `/muscle-volume` that genuinely
        * returns nothing would sit as one sentence in a 534px box, a 490px hole, measured. So the
        * empty answer takes its natural height and only the wait is reserved.
        *
        * And the reserve is the SHORTER of the card's two loaded shapes — 409px, the one drawn for
        * a client whose volume ring has no honest denominator, rather than the 534 of the one with
        * it. Both numbers are real; a constant can only match one, and reserving the taller made
        * the card collapse 124.56px for the clients on the shorter shape. Growth and collapse both
        * move nothing here, but a collapse is a hole that was on screen for half a second and a
        * growth is not. */}
      <section
        className={`${styles.card} ${volumeGate.waiting ? styles.reserveVolume : ''}`}
        aria-busy={volumeGate.waiting}
      >
        {/* The wait first, the failure inside the not-waiting arm: the same ordering as the
          * attendance card above, and for the same two reasons. */}
        {volumeGate.waiting ? (
          <>
            <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
            {volumeGate.skeleton ? (
              <span className={styles.skelVolume}>
                <i className={`${String(skel.bar)} ${String(styles.skelSilhouette)}`} />
                <span className={styles.skelVolumeRows}>
                  {[0, 1, 2, 3, 4].map((slot) => (
                    <i key={slot} className={`${String(skel.bar)} ${String(styles.skelVolumeRow)}`} />
                  ))}
                </span>
              </span>
            ) : null}
          </>
        ) : volume.isError ? (
          <p className={`${String(styles.notice)} ${volumeGate.reveal}`}>
            {t('clients.detail.states.errorMessage')}
          </p>
        ) : muscles.clusters.length === 0 ? (
          /* NEUTRAL: no volume registered in this window is a statement about the range the
           * trainer chose, and the range bar above is where that is changed. */
          <EmptyState
            className={volumeGate.reveal}
            mark={<VolumeMarkIcon />}
            tone="neutral"
            title={t('web:clientDetail.volume.empty')}
          />
        ) : (
          <div className={`${String(styles.volumeRow)} ${volumeGate.reveal}`}>
            <div className={styles.figureColumn}>
              {/* The volume ring, on the section it summarises. Owner, 1 September 2026: "przenieś
                * ten krążek jakoś ładnie koło objętości mięśniowej." Concentric with the attendance
                * ring on the card above, the fraction had nothing near it to explain what it was
                * about; here the MEV/MAV tracks that set its denominator are the next thing under
                * it, and the silhouettes beside it are the same groups counted. */}
              {rings.volume === null ? null : (
                <section className={styles.ringBlock}>
                  <em className={styles.ringEyebrow}>
                    {t('web:clientDetail.attendance.rings.volumeName')}
                  </em>
                  <div className={styles.ringColumn}>
                    <ReadoutRing
                      percent={rings.volume.pct}
                      diameter={RING.diameter}
                      thickness={RING.thickness}
                      durationMs={RING.durationMs}
                      scale="wide"
                      colour="var(--chart-series-neutral)"
                      figure={t('web:clientDetail.attendance.rings.pct', { pct: rings.volume.pct })}
                      label={t('web:clientDetail.attendance.rings.volume', {
                        value: rings.volume.value,
                        total: rings.volume.total,
                        pct: rings.volume.pct,
                      })}
                      reducedMotion={reducedMotion}
                    />
                    <p className={styles.ringDenominator}>
                      {t('web:clientDetail.attendance.rings.ofGroups', {
                        value: rings.volume.value,
                        count: rings.volume.total,
                      })}
                    </p>
                  </div>
                </section>
              )}

              {/* The bars below carry the same numbers with words beside them, so the silhouettes
                * are hidden from assistive technology rather than given a giant label nobody can
                * parse. Mobile takes the same decision for the same reason. */}
              <div className={styles.figureRow} aria-hidden="true">
                <figure className={styles.figureBox}>
                  <BodySilhouette
                    side="front"
                    gender={gender}
                    fillBySlug={fillsFor(muscles.front)}
                    baseFill={ZONE_FILL.none}
                  />
                  <figcaption className={styles.figureCaption}>
                    {t('clients.detail.muscleMap.front')}
                  </figcaption>
                </figure>
                <figure className={styles.figureBox}>
                  <BodySilhouette
                    side="back"
                    gender={gender}
                    fillBySlug={fillsFor(muscles.back)}
                    baseFill={ZONE_FILL.none}
                  />
                  <figcaption className={styles.figureCaption}>
                    {t('clients.detail.muscleMap.back')}
                  </figcaption>
                </figure>
              </div>

              {/* The legend stacks under the figures inside their own column, where it reads as a
                * key to them. Across the card's foot it read as a fifth section. */}
              <ul className={styles.zoneLegendList}>
                {ZONES.map((zone) => {
                  const entry = splitZoneLegend(zone, t(`clients.detail.progress.legend.${zone}`));
                  return (
                    <li key={zone} className={styles.zoneLegendItem}>
                      <i
                        className={`${String(styles.zoneSwatch)} ${String(styles[`${zone}Fill`])} ${
                          zone === 'none' ? String(styles.zoneSwatchNone) : ''
                        }`}
                        aria-hidden="true"
                      />
                      {entry.range === '' ? null : <b className={styles.zoneRange}>{entry.range}</b>}
                      {entry.rest}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className={styles.clusters}>
              {muscles.clusters.map((cluster) => (
                <section key={cluster.key} className={styles.cluster}>
                  <h3 className={styles.clusterHead}>{cluster.label}</h3>
                  <ul className={styles.muscleList}>
                    {cluster.rows.map((row) => (
                      <li key={row.key} className={styles.muscleRow} aria-label={row.ariaLabel}>
                        <span className={styles.muscleName}>{row.label}</span>
                        <span className={styles.muscleTrack}>
                          <i
                            className={`${String(styles.muscleFill)} ${String(styles[`${row.zone}Fill`])}`}
                            style={{ inlineSize: `${String(row.fillPct)}%` }}
                          />
                          {/* The two threshold marks the canon rules the track with: MEV and MRV.
                            * They are what make a bar readable as a POSITION between bands rather
                            * than as a fraction of an arbitrary maximum. */}
                          <i
                            className={styles.muscleTick}
                            style={{ insetInlineStart: `${String(row.mevPct)}%` }}
                          />
                          <i
                            className={styles.muscleTick}
                            style={{ insetInlineStart: `${String(row.mrvPct)}%` }}
                          />
                        </span>
                        <b className={styles.muscleSets}>
                          {t('web:clientDetail.volume.clusterSets', { count: row.weeklySets })}
                        </b>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </div>
        )}

        {muscles.otherSets === 0 ? null : (
          <p className={styles.hint}>
            {t('web:clientDetail.volume.other', { count: muscles.otherSets })}
          </p>
        )}
      </section>
    </>
  );
}

function ChartCard({
  id,
  eyebrow,
  caption,
  series,
  colour,
  banding,
  unit,
  zeroBaseline,
  xDomain,
  scale,
  onScaleChange,
  failed,
  gate,
  reducedMotion,
}: {
  /** This chart's name inside the sync provider. See `readoutSync`. */
  id: string;
  eyebrow: string;
  caption: string;
  series: TrendSeries;
  colour: string;
  /**
   * How the line is banded, for a quantity that has thresholds. Omitted on tonnage, which has none
   * — a total is neither good nor bad at any particular number.
   */
  banding?: ChartBanding;
  /** The word the gutter puts after every figure — `t`, `h`. Omitted for a dimensionless rating. */
  unit?: string;
  zeroBaseline: boolean;
  /** The span every chart on the tab is drawn over, so the three line up. `null` while empty. */
  xDomain: readonly [Date, Date] | null;
  /**
   * The scale this card is showing, and the setter for it. Both omitted on a card that has only
   * one scale — tonnage is a weekly total and there are no individual readings behind it to offer.
   */
  scale?: TrendScale;
  onScaleChange?: (scale: TrendScale) => void;
  failed: boolean;
  /**
   * The three cards' shared wait, from the screen: one read behind them, so one gate. It picks the
   * branch, the bars and the `aria-busy` the shell publishes. Separate from `series.empty`, which
   * is also true while loading — see the render below for why the two must not share a branch.
   */
  gate: SkeletonGate;
  reducedMotion: boolean;
}) {
  const { t } = useTranslation();

  return (
    /* The four states and the plot's reserve are `shared/ui/chart/ChartCardShell` — the same shell
     * the Ankiety tab's strip stands in. What stays here is what is actually Trendy's: the head,
     * the scale switch under the plot, the caption and the failure sentence. */
    <ChartCardShell
      className={styles.card}
      height="var(--h-chart-plot)"
      gate={gate}
      failed={failed}
      /* `series.empty` is true both while the read is in flight and when it comes back with
       * nothing, and those two want different words — which is why the shell takes them as two
       * separate questions rather than branching on the data. */
      empty={series.empty || xDomain === null}
      emptyTitle={t('web:clientDetail.trends.empty')}
      failedTitle={t('web:clientDetail.trends.failed')}
      head={
        /* The head keeps the figure's line box whether or not there is a figure in it. `chartFigure`
         * is `--fs-session` extrabold and the eyebrow beside it is 10px, so a head with no reading
         * yet stood 12px where 19 was coming — 7px the whole card moved down by when the response
         * landed (measured 1 September 2026). It is the same rule as the plot's reserve. */
        <div className={`${styles.cardHead} ${styles.chartHead}`}>
          <em className={eyebrows.eyebrow}>{eyebrow}</em>
          {series.latest === null ? null : (
            <>
              <b className={styles.chartFigure}>{series.latest}</b>
              {/* A week that has not finished is not a week that went badly. Two days measured
                * against seven printed "−22,0 vs poprzednia" — a collapse in training produced by
                * the calendar. The figure stays, because a partial total is true; the comparison
                * beside it was the part that was not.
                *
                * Which "previous" the delta names comes off the SERIES, not off this component's
                * own knowledge of which switch is thrown: at the every-reading scale it is the
                * reading before, and calling that "vs poprzednia [tydzień]" would name a period the
                * chart is not drawing. */}
              {series.inProgress ? (
                <em className={styles.chartDelta}>{t('web:clientDetail.trends.weekInProgress')}</em>
              ) : series.delta === null ? null : (
                <em
                  className={`${String(styles.chartDelta)} ${series.better === true ? String(styles.better) : series.better === false ? String(styles.worse) : ''}`}
                >
                  {t(
                    series.basis === 'week'
                      ? 'web:clientDetail.trends.vsPrevious'
                      : 'web:clientDetail.trends.vsPreviousReading',
                    { delta: series.delta },
                  )}
                </em>
              )}
            </>
          )}
        </div>
      }
      /* ── The scale switch sits UNDER the plot, and that placement is measured ──────────────
       * Above it, the switch pushed the sleep and energy plots 30px down the card while tonnage —
       * which has only one scale and therefore no switch — kept its own y. The three plots then
       * stood in three horizontal bands, and a crosshair synchronised across them could no longer
       * be followed as one vertical line, which is the whole reason the three cards sit in one row.
       * Under the plot every card's plot box starts at the same y and the short card simply ends
       * earlier inside its stretched box.
       *
       * It is drawn in every state but a FAILURE, the two short ones included: it is a control over
       * the chart rather than a part of the answer, it is operable before the response lands, and
       * taking it away while a request is open would move it by its own height in the frame the
       * answer arrives. A failed read is the one case it comes off, because there are then no
       * readings at either scale to switch between.
       *
       * A RADIO GROUP because it is one choice among two mutually exclusive views, and Base UI
       * supplies the whole of that behaviour — the roving tab stop, the arrow keys, `aria-checked`,
       * and the hidden input that carries the value. Every visual byte is ours. */
      below={
        scale === undefined || onScaleChange === undefined ? null : (
          <ScaleToggle name={eyebrow} scale={scale} onScaleChange={onScaleChange} />
        )
      }
      /* It describes what the chart counts rather than what this client did, so it is true before
       * the data arrives and it stands in every state. Rendered on the loaded branch alone, it put
       * 34 more pixels into the card at the moment the response landed and moved the volume section
       * under it by exactly that (measured 1 September 2026). */
      caption={<em className={styles.caption}>{caption}</em>}
    >
      {/* The shell's `children` is a plain node now that the gate is the caller's, so the
        * hand-over class goes on the chart here rather than coming back out of the shell. */}
      <ReadoutChart
        className={gate.reveal}
        points={series.points}
        colour={colour}
        height="var(--h-chart-plot)"
        name={eyebrow}
        unit={unit}
        zeroBaseline={zeroBaseline}
        xDomain={xDomain ?? undefined}
        banding={banding}
        provisionalTail={series.inProgress}
        syncId={id}
        reducedMotion={reducedMotion}
      />
    </ChartCardShell>
  );
}

/**
 * The weekly-average / every-reading switch, one per wellbeing card.
 *
 * ── Why it is not promoted to `shared/ui` ────────────────────────────────────────────────────
 * Two call sites, both in this file, both the same question about the same kind of series. The
 * house rule promotes on the SECOND CONSUMER, and a second consumer means a second surface — the
 * plan builder's family pills and the roster's filter tabs are each a segmented control too, and
 * each is already styled to its own surface's geometry. Lifting this now would be a shared module
 * with one caller wearing three sets of measurements.
 *
 * ── The accessible name is the chart's own eyebrow ───────────────────────────────────────────
 * Two identical groups on one screen, both reading "Skala", is two controls a screen-reader user
 * cannot tell apart. The group is named for the chart it governs — "Skala wykresu: Sen" — while
 * each option keeps its own visible words as its name, so 2.5.3 holds without an `aria-label`
 * replacing anything.
 */
function ScaleToggle({
  name,
  scale,
  onScaleChange,
}: {
  readonly name: string;
  readonly scale: TrendScale;
  readonly onScaleChange: (scale: TrendScale) => void;
}) {
  const { t } = useTranslation();

  return (
    <RadioGroup
      className={styles.scaleGroup}
      aria-label={t('web:clientDetail.trends.scaleLabel', { name })}
      value={scale}
      onValueChange={(next) => {
        // Base UI types the value as the group's generic; it can only be one of the two rendered
        // radios, and narrowing here keeps the cast out of the setter.
        if (next === 'weekly' || next === 'readings') onScaleChange(next);
      }}
    >
      {(['weekly', 'readings'] as const).map((option) => (
        <Radio.Root key={option} value={option} className={styles.scaleOption}>
          {t(option === 'weekly' ? 'web:clientDetail.trends.scaleWeekly' : 'web:clientDetail.trends.scaleReadings')}
        </Radio.Root>
      ))}
    </RadioGroup>
  );
}
